import React, {useState, useEffect} from "react";
import { Link, useNavigate } from "react-router-dom";
import config from "../../utils/config.js";

import { useStepContext } from "../../utils/StepContext.jsx"
import { useFormContext } from "../../utils/FormContext.jsx"; 
import { useMediaQuery } from "react-responsive";
import { useAuth } from '../../utils/AuthContext/AuthContext';  
import { getSessionId } from '../../utils/session';

import MainCard from "../MainCard.jsx";
import Modal from "../Modal.jsx"
import ProgressBar from "../ProgressBar/ProgressBar.jsx";
import SaveButton from "../SaveButton.jsx";
import HelpButton from "../HelpButton.jsx";
import ShishitoAssist from "../ShishitoAssist/ShishitoAssist.jsx";
import ReContractAnalyzerAgent from "../PDFAnalyzer/ReContractAnalyzerAgent.jsx";
import EditSVG from "../../assets/Edit.jsx";
import AISVG from "../../assets/AI.jsx";

const FinalReview = () => {
    const { handleNext, handleBack, setCurrentStep, currentStep, markStepAsComplete } = useStepContext();
    const { formData, propertyData, formId, handleSendtoSellerCheckbox, handleSave, handleTermsAndConditionsCheckbox, setShowConfirmationModal } = useFormContext(); 
    const navigate = useNavigate();
    const sessionId = getSessionId();

    const currentPageValue = 3;
    const currentSubStepProgress = 1/1; 
    const isEmailValid = formData.listingAgentEmail; 
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const { user } = useAuth();
    const [isAssistVisible, setIsAssistVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showAnalyzer, setShowAnalyzer] = useState(false);
    const [analysisFileUrl, setAnalysisFileUrl] = useState("");
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const [latestFileKey, setLatestFileKey] = useState(null);
    
    const handleSaveAndNavigate = async () => {
      await handleSave();
      navigate('/packet/sign');
  };

  const onBack = () => {
    unsetStepAsComplete(16); // Assuming PacketSign is step 16
    handleBack();
};

const onNext = async () => {
  setLoading(true);

  if (!user) {
    console.error('No user data available.');
    setShowConfirmationModal(true);
    setLoading(false); 
    return;
}

  markStepAsComplete(16); // Mark this step as complete
  handleNext(); // Navigate to the next step
  await handleSave(); 
  setLoading(false); 

  if (window?.newrelic) {
    window.newrelic.recordCustomEvent('AnalyticEvent', {
      element: 'signButton',
      action: 'signClick',
      email: user?.email || 'unknown',
      sessionId: sessionId
    });
  }
};

const toggleAssist = () => {
  setIsAssistVisible(!isAssistVisible);
};

function convertTo12Hour(time) {
  let [hours, minutes] = time.split(':');
  let period = +hours < 12 ? 'AM' : 'PM';
  hours = +hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
}

    const getComponentIndex = (title) => {
      const mapping = {
          "Parties and Property - Buyer Information": 2,
          "Parties and Property - Seller Information": 3,
          "Parties and Property - Additional Buyers Information": 4,
          "Other Property Items": 5,
          "Dates & Deadlines": 7,
          "Price & Terms": 8,
          "Price & Terms - Seller Concession": 9,
          "Title Insurance & Record Title": 10,
          "New ILC, New Survey": 11,
          "Disclosure, Inspection, & Due Diligence": 12,
          "Closing Costs": 13,
          "Prorations": 14,
          "Possession": 15,
          "Additional Provisions": 16,
      };
      return mapping[title] || 2; // Default to 0 (e.g., SearchCard.jsx) or some safe fallback
  };

  const handleEditClick = (title) => {
      const index = getComponentIndex(title);
      setCurrentStep(index);
  };

    // This function fills the PDF and gets a public URL from backend.
    const handleAISummarize = async () => {
      setAnalyzeLoading(true);
      setAnalysisError(null);
      try {
        const response = await fetch(`${config.apiBaseUrl}/fill-pdf-for-ai`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ formData, formId, propertyData })
        });
        if (!response.ok) {
          throw new Error("Failed to fill PDF for AI analysis");
        }
        const data = await response.json();
        setAnalysisFileUrl(data.fileUrl);
        setLatestFileKey(data.fileKey);
        setShowAnalyzer(true);
      } catch (err) {
        console.error(err);
        setAnalysisError(err.message);
      } finally {
        setAnalyzeLoading(false);
      }
    };

  // console.log("Form Data", formData);
    const sections = [
      {
          title: "Parties and Property - Buyer Information",
          fields: [
              { label: "Your Name", value: `${formData.firstName} ${formData.lastName}` },
              { label: "Your Address", value: Object.values(formData.buyer1HomeAddress).join(', ')},
              { label: "Your Phone Number", value: formData.buyer1Phone },
              { label: "Your Email", value: formData.buyer1Email },
          ]
      },
      {
          title: "Parties and Property - Seller Information",
          fields: [
              { label: "Seller 1 Name", value: `${formData.seller1Name}` },
              { label: "Seller 2 Name", value: formData.seller2Name },
              { label: "Seller Address", value: Object.values(formData.address).join(', ')},
              { label: "Listing Agent Name", value: formData.listingAgentName },
              { label: "Listing Agent Email", value: formData.listingAgentEmail },
              { label: "Listing Agent Phone", value: formData.listingAgentPhone },
          ]
      },
      {
          title: "Parties and Property - Additional Buyers Information",
          fields: [
          { 
              label: "Purchasing with Someone Else", 
              value: typeof formData?.additionalTenants === 'string' 
                  ? formData.additionalTenants.replace(/\b\w/g, char => char.toUpperCase()) 
                  : '' // Fallback value if additionalTenants is not a string
          },
          { 
              label: "Purchasing As", 
              value: Object.entries(formData.tenantType)
                  .filter(([key, value]) => value)
                  .map(([key]) => key)
                  .join(', ')
                  .replace(/\b\w/g, char => char.toUpperCase())
          },
            {
                label: "Additional Buyer Information",
                render: () => formData.tenants && formData.tenants.map((tenant, index) => (
                  <div key={index} className="flex flex-col gap-2 ml-2">
                    <span className="text-shi-green font-semibold">Buyer Name: {`${tenant.tenantFirstName} ${tenant.tenantLastName}`}</span>
                    <span className="text-shi-green font-semibold">Buyer Email: {`${tenant.tenantEmail}`}</span>
                    <span className="text-shi-green font-semibold">Buyer Phone: {`${tenant.tenantPhone}`}</span>
                  </div>
                ))
            },
          ]
      },
      {
        title: "Other Property Items",
        fields: [
            { label: "Adding Additional Property Information", value: `${formData.additionalPropertyInformation}` },
            { label: "Attached Inclusions", value: 
              `
              ${formData?.inclusionsPreStep?.solarPanels?.included === true ? 'Solar Panels,' : ''}
              ${formData?.inclusionsPreStep?.waterSofteners?.included === true ? 'Water Softeners,' : ''}
              ${formData?.inclusionsPreStep?.securitySystems?.included === true ? 'Security Systems,' : ''}
              ${formData?.inclusionsPreStep?.satelliteSystems?.included === true ? 'Satellite Systems,' : ''}
              ` },
            { label: "Solar Power Plan", value: formData?.inclusionsPreStep?.solarPanels?.solarPowerPlan === true ? 'Solar Power Plan Included' : 'Solar Power Plan Not Included' },
            { label: "Assumption of Solar Power Plan", value: formData?.inclusionsPreStep?.solarPanels?.assumeSolarObligations === true ? 'Assuming Solar Power Plan Obligation' : 'Not Assuming Solar Power Plan Obligation' },
            { label: "Leased Items Responsibility", value: formData.leasedItems.replace(/\b\w/g, char => char.toUpperCase()) },
            { label: "Encumbered Inclusion Responsibility", value: formData.encumberedInclusions.replace(/\b\w/g, char => char.toUpperCase()) },
            { label: "Other Inclusions", value: 
              `
              ${formData.otherInclusionsText} 
            ` },
            { label: "Encumbered Inclusions", value: 
              `
              ${formData?.inclusionsPreStep?.encumberedInclusionsValues + ', ' + formData?.inclusionsPreStep?.encumberedInclusionsManualText} 
            ` },
            { label: "Parking Facilities", value: 
              `
              ${formData.parkingFacility} 
            ` },
            { label: "Storage Facilities", value: 
              `
              ${formData.storageFacility} 
            ` },
            { label: "Leased Items", value: 
              `
              ${formData?.inclusionsPreStep?.leasedItemsValues + ', ' + formData?.inclusionsPreStep?.leasedItemsManualText}  
            ` },
            { label: "Exclusions", value: 
              `
              ${formData.exclusionsText} 
            ` },
            { label: "Water Rights", value: 
              `${formData?.inclusionsPreStep?.waterRights?.included === true ? 'Yes' : 'No'}` 
             },
            ...(formData?.inclusionsPreStep?.waterRights?.included === true ? [
            { label: "Deeded Water Rights", value: 
              `
              ${formData.deededWaterRights === 'yes' ? formData.deededWaterRightsDescription : 'N/A'} 
            ` },
            { label: "Water Rights Deed", value: 
              `
              ${formData.typeOfDeed !== 'N/A' ? formData.typeOfDeed : 'N/A'} 
            ` },
            { label: "Other Water Rights", value: 
              `
              ${formData.otherWaterRights === 'yes' ? formData.otherWaterRightsDescription : 'N/A'} 
            ` },
            ...formData.isWell === 'yes' ? [
            { label: "Water Well on Property", value: 
              `
              ${formData.isWell === 'yes' ? formData.isWell : ''} 
            ` },
            ] : [],
            ...formData.copyOfWellPermit === 'yes' ? [
            { label: "Copy of Well Permit", value: 
              `
              ${formData.copyOfWellPermit === 'yes' ? formData.copyOfWellPermit : ''} 
            ` },
            ] : [],
            ...formData.wellRights === 'yes' ? [
            { label: "Well Rights", value: 
              `
              ${formData.wellRights === 'yes' ? formData.wellRights : ''}
              Well Permit #: ${formData.wellPermitNumber} 
            ` },
            ] : [],
            { label: "Source of Water Addendum Received?", value: 
              `
              ${formData.waterAddendumCertificate === 'yes' ? formData.waterAddendumCertificate : ''} 
            ` },
            { label: "Water Stock Certificate", value: 
              `
              ${formData.waterStockCertificateDescription} 
            ` },
            // { label: "Terminate for Unsatisfactory Water Exam", value: 
            //   `
            //   ${formData.waterRightsReview.replace(/\b\w/g, char => char.toUpperCase())} 
            // ` },
            ] : []),
        ]
    },
      {
          title: "Dates & Deadlines",
          fields: [
            ...formData.dateFields.closingTimeframe !== '' ? [
              { label: "Closing Timeframe Auto Fill", value: formData.dateFields.closingTimeframe },
            ] : [],
              { sectionLabel: "GENERAL" },
              { 
                label: "Time Deadline to Send Contract", 
                value: `${formData.dateFields.time_deadline_to_send_contract.daysFromMEC}`
              },
              { 
                label: "Alternative Earnest Money Deadline", 
                // value: `${formData.dateFields.alternative_earnest_money_deadline.dayOfWeek}` + ' ' + new Date(formData.dateFields.alternative_earnest_money_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.alternative_earnest_money_deadline.daysFromMEC}`
              }, 
              { sectionLabel: "TITLE" },
              { 
                label: "Record Title Deadline", 
                // value: `${formData.dateFields.record_title_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.record_title_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.record_title_deadline.daysFromMEC}`
              },
              { 
                label: "Record Title Objection Deadline", 
                // value: `${formData.dateFields.record_title_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.record_title_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.record_title_objection_deadline.daysFromMEC}`
              },
              { 
                label: "Off Record Title Deadline", 
                // value: `${formData.dateFields.off_record_title_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.off_record_title_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.off_record_title_deadline.daysFromMEC}`
              },
              { 
                label: "Off Record Title Objection Deadline", 
                // value: `${formData.dateFields.off_record_title_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.off_record_title_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.off_record_title_objection_deadline.daysFromMEC}`
              },
              { 
                label: "Title Resolution Deadline", 
                // value: `${formData.dateFields.title_resolution_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.title_resolution_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.title_resolution_deadline.daysFromMEC}`
              },
              { 
                label: "Right of First Refusal Deadline", 
                // value: `${formData.dateFields.right_of_first_refusal_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.right_of_first_refusal_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.right_of_first_refusal_deadline.daysFromMEC}`
              },
              { sectionLabel: "OWNERS ASSOCIATION" },
              { 
                label: "Association Document Deadline", 
                // value: formData.isHOA === true ? `${formData.dateFields.association_document_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.association_document_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
                value: formData.isHOA === true ? `${formData.dateFields.association_document_deadline.daysFromMEC}` : 'N/A'
              },
              { 
                label: "Association Documents Termination Deadline", 
                // value: formData.isHOA === true ? `${formData.dateFields.association_documents_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.association_documents_termination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
                value: formData.isHOA === true ? `${formData.dateFields.association_documents_termination_deadline.daysFromMEC}` : 'N/A'
              },
              { sectionLabel: "SELLERS DISCLOSURES" },
              { 
                label: "Seller Property Disclosure Deadline", 
                // value: `${formData.dateFields.seller_property_disclosure_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.seller_property_disclosure_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.seller_property_disclosure_deadline.daysFromMEC}`
              },
              { 
                label: "Lead Based Paint Disclosure Deadline", 
                // value: `${formData.dateFields.lead_based_paint_disclosure_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.lead_based_paint_disclosure_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.lead_based_paint_disclosure_deadline.daysFromMEC}`
              },
              { sectionLabel: "LOAN & CREDIT" },
              { 
                label: "New Loan Application Deadline", 
                // value: `${formData.dateFields.new_loan_application_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_loan_application_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_loan_application_deadline.daysFromMEC}`
              },
              { 
                label: "New Loan Terms or Termination Deadline", 
                // value: `${formData.dateFields.new_loan_terms_or_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_loan_terms_or_termination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_loan_terms_or_termination_deadline.daysFromMEC}`
              },
              { 
                label: "New Loan Availability Deadline", 
                // value: `${formData.dateFields.new_loan_availability_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_loan_availability_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_loan_availability_deadline.daysFromMEC}`
              },
              { 
                label: "Buyers Credit Information Deadline", 
                // value: `${formData.dateFields.buyers_credit_information_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.buyers_credit_information_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.buyers_credit_information_deadline.daysFromMEC}`
              },
              { 
                label: "Disapproval of Buyers Credit Information Deadline", 
                // value: `${formData.dateFields.disapproval_of_buyers_credit_information_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.disapproval_of_buyers_credit_information_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.disapproval_of_buyers_credit_information_deadline.daysFromMEC}`
              },
              { 
                label: "Existing Loan Deadline", 
                // value: `${formData.dateFields.existing_loan_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.existing_loan_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.existing_loan_deadline.daysFromMEC}`
              },
              { 
                label: "Existing Loan Termination Deadline", 
                // value: `${formData.dateFields.existing_loan_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.existing_loan_termination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.existing_loan_termination_deadline.daysFromMEC}`
              },
              { 
                label: "Loan Transfer Approval Deadline", 
                // value: `${formData.dateFields.loan_transfer_approval_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.loan_transfer_approval_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.loan_transfer_approval_deadline.daysFromMEC}`
              },
              { 
                label: "Seller or Private Financing Deadline", 
                // value: `${formData.dateFields.seller_or_private_financing_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.seller_or_private_financing_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.seller_or_private_financing_deadline.daysFromMEC}`
              },
              { sectionLabel: "APPRAISAL"},
              { 
                label: "Appraisal Deadline", 
                // value: `${formData.dateFields.appraisal_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.appraisal_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.appraisal_deadline.daysFromMEC}`
              },
              { 
                label: "Appraisal Objection Deadline", 
                // value: `${formData.dateFields.appraisal_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.appraisal_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.appraisal_objection_deadline.daysFromMEC}`
              },
              { 
                label: "Appraisal Resolution Deadline", 
                // value: `${formData.dateFields.appraisal_resolution_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.appraisal_resolution_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.appraisal_resolution_deadline.daysFromMEC}`
              },
              { sectionLabel: "SURVEY"}, 
              { 
                label: "New ILC or New Survey Deadline", 
                // value: `${formData.dateFields.new_ilc_or_new_survey_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_ilc_or_new_survey_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_ilc_or_new_survey_deadline.daysFromMEC}`
              },
              { 
                label: "New ILC or New Survey Objection Deadline", 
                // value: `${formData.dateFields.new_ilc_or_new_survey_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_ilc_or_new_survey_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_ilc_or_new_survey_objection_deadline.daysFromMEC}`
              },
              { 
                label: "New ILC or New Survey Resolution Deadline", 
                // value: `${formData.dateFields.new_ilc_or_new_survey_resolution_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.new_ilc_or_new_survey_resolution_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.new_ilc_or_new_survey_resolution_deadline.daysFromMEC}`
              },
              { sectionLabel: "INSPECTION & DUE DILIGENCE"}, 
              { 
                label: "Water Rights Examination Deadline", 
                // value: formData.addWaterAndMineralDates === true ? `${formData.dateFields.water_rights_examination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.water_rights_examination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
                value: formData.addWaterAndMineralDates === true ? `${formData.dateFields.water_rights_examination_deadline.daysFromMEC}` : 'N/A'
              },
              { 
                label: "Mineral Rights Examination Deadline", 
                // value: formData.addWaterAndMineralDates === true ? `${formData.dateFields.mineral_rights_examination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.mineral_rights_examination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'
                value: formData.addWaterAndMineralDates === true ? `${formData.dateFields.mineral_rights_examination_deadline.daysFromMEC}` : 'N/A'
              },
              { 
                label: "Inspection Termination Deadline", 
                // value: `${formData.dateFields.inspection_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.inspection_termination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.inspection_termination_deadline.daysFromMEC}`
              },
              { 
                label: "Inspection Objection Deadline", 
                // value: `${formData.dateFields.inspection_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.inspection_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.inspection_objection_deadline.daysFromMEC}`
              },
              { 
                label: "Inspection Resolution Deadline", 
                // value: `${formData.dateFields.inspection_resolution_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.inspection_resolution_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.inspection_resolution_deadline.daysFromMEC}`
              },
              { 
                label: "Property Insurance Termination Deadline", 
                // value: `${formData.dateFields.property_insurance_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.property_insurance_termination_deadline.date+ 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.property_insurance_termination_deadline.daysFromMEC}`
              },
              { 
                label: "Due Diligence Documents Delivery Deadline", 
                // value: `${formData.dateFields.due_diligence_documents_delivery_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.due_diligence_documents_delivery_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.due_diligence_documents_delivery_deadline.daysFromMEC}`
              },
              { 
                label: "Due Diligence Documents Objection Deadline", 
                // value: `${formData.dateFields.due_diligence_documents_objection_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.due_diligence_documents_objection_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.due_diligence_documents_objection_deadline.daysFromMEC}`
              },
              { 
                label: "Due Diligence Documents Resolution Deadline", 
                // value: `${formData.dateFields.due_diligence_documents_resolution_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.due_diligence_documents_resolution_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.due_diligence_documents_resolution_deadline.daysFromMEC}`
              },
              { 
                label: "Conditional Sale Deadline", 
                // value: `${formData.dateFields.conditional_sale_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.conditional_sale_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.conditional_sale_deadline.daysFromMEC}`
              },
              { 
                label: "Lead Based Paint Termination Deadline", 
                // value: `${formData.dateFields.lead_based_paint_termination_deadline.dayOfWeek}` + ' ' + new Date (formData.dateFields.lead_based_paint_termination_deadline.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.lead_based_paint_termination_deadline.daysFromMEC}`
              },
              { 
                label: "Closing Date", 
                // value: `${formData.dateFields.closing_date.dayOfWeek}` + ' ' + new Date (formData.dateFields.closing_date.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.closing_date.daysFromMEC}`
              },
              { 
                label: "Possession Date", 
                // value: `${formData.dateFields.possession_date.dayOfWeek}` + ' ' + new Date (formData.dateFields.possession_date.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.possession_date.daysFromMEC}`
              },
              { 
                label: "Possession Time", 
                // value: convertTo12Hour(formData.dateFields.possession_time.date)
                value: `${formData.dateFields.possession_time.daysFromMEC}`
              },
              { 
                label: "Acceptance Deadline Date", 
                // value: `${formData.dateFields.acceptance_deadline_date.dayOfWeek}` + ' ' + new Date (formData.dateFields.acceptance_deadline_date.date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                value: `${formData.dateFields.acceptance_deadline_date.daysFromMEC}`
              },
              {             
                label: "Acceptance Deadline Time", 
                // value: convertTo12Hour(formData.dateFields.acceptance_deadline_time.date)
                value: `${formData.dateFields.acceptance_deadline_time.daysFromMEC}`
              },
              // Add more date-related fields here
          ]
      },
      {
        title: "Price & Terms", 
        fields: [
          { label: "Purchase Price", value: `$ ${formData.prices[0].purchasePrice}` },
          { label: "Earnest Money", value: `$ ${formData.prices[0].earnestMoney}` },
          { label: "New Loan Amount", value: `$ ${formData.prices[0].newLoanAmount}` },
          { label: "Cash at Closing", value: `$ ${formData.prices[0].cashAtClosing}` },
          { label: "Cash at Closing Verified?", value: formData.isCashAtClosingVerified.replace(/\b\w/g, char => char.toUpperCase()) }, 
          { label: "Earnest Money Payable By", value: formData.earnestMoneyPayable.replace(/\b\w/g, char => char.toUpperCase())},
          { label: "Loan Type", 
          render: () => (
              Object.entries(formData.loanType).map(([key, value]) => (
                  value && (
                      <div key={key} className="loan-type-item">
                          {/* Render a span with the key as its content */}
                          <span className="text-shi-green font-semibold">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          {/* If the key is "other" and the value is true, display formData.loanTypeOtherInput */}
                          {key === "other" && value && (
                              <span className="mx-2 text-shi-green font-semibold">: {formData.loanTypeOtherInput}</span>
                          )}
                      </div>
                  )
              ))
          )
          },
        ]
      },
      {
        title: "Price & Terms - Seller Concession", 
        fields: [
          { label: "Seller Concession", value: '$ '+formData.sellerConcession},
        ]
      },
      {
        title: "Title Insurance & Record Title", 
        fields: [
          { label: "Will Seller Select Title Insurance Company?", value: formData.sellerSelectsTitleCo.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Will Title Contain Owner's Extended Coverage (OEC)", value: formData.ownersExtendedCoverage.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "OEC To Be Paid By", value: formData.oecPayment.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "OEC Other Text", value: formData.oecPaymentOther.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Transfer of Title", value: formData.transferOfTitle.replace(/\b\w/g, char => char.toUpperCase()) },
        ]
      },
      {
        title: "New ILC, New Survey", 
        fields: [
          { label: "New Improvement Location Certificate or Survey?", value: formData.ilcOrSurvey.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Who Will Pay for ILC or Survey", value: formData.ilcPayment.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "ILC Payment Option", value: formData.ilcPaymentText.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Who Will Order the ILC or Survey", value: formData.ilcOrder.replace(/\b\w/g, char => char.toUpperCase()) },
        ]
      },
      {
        title: "Disclosure, Inspection, & Due Diligence", 
        fields: [
          // { label: "Mineral Rights Review", value: formData.mineralRightsReview.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Is This Contract Conditional Upon Sale of Existing Property?", value: formData.conditionalUponSale.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Due Diligence Documents", value: formData.dueDiligenceDocuments === 'yes' ? formData.dueDiligenceDocumentsText : 'N/A' },
          { label: "Due Diligence Documents", value: formData.occupancyAgreements === 'yes' ? formData.occupancyAgreementsText : 'N/A' },
          { label: "Other Documents", value: formData.otherDocuments === 'yes' ? formData.otherDocumentsText : 'N/A' },
        ]
      },
      {
        title: "Closing Costs", 
        fields: [
          { label: "Standard Closing Costs Option", value: formData.standardClosingCosts === 'yes' ? formData.standardClosingCosts.replace(/\b\w/g, char => char.toUpperCase()) : 'No'},
          { label: "Closing Services Fee", value: formData.closingServicesFee.replace(/\b\w/g, char => char.toUpperCase()) },
          ...formData.closingServicesFee === 'other' ? [
          { label: "Closing Service Fee Other Input", value: formData.closingServicesFee === 'other' ? formData.closingServicesFeeOtherInput : 'N/A'}, 
          ] : [],
          { label: "Status Letter Fee", value: formData.statusLetterFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Record Change Fee", value: formData.recordChangeFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Appraisal Fee", value: formData.appraisalFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Assessment Fees", value: formData.assessmentFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Other Fee", value: formData.otherFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Local Transfer Tax", value: formData.transferTax.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Sales and Use Tax", value: formData.salesAndUseTax.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Private Transfer Fee", value: formData.privateTransferFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Water Fees Do Not Exceed", value: `$ ${formData.waterTransferFeeAmount} for ${formData.waterStockCert === 'yes' ? 'Water Stock / Certificate' : ''} ${formData.waterDistrict === 'yes' ? 'Water District' : ''} ${formData.augmentationMembership === 'yes' ? 'Augmentation Membership' : ''} ${formData.smallDomesticWaterCo === 'yes' ? 'Small Domestic Water Co.' : ''} ${formData.waterTransferFeeOther === 'yes' ? 'Other Water Transfer Option' : ''} `},
          ...formData.waterTransferFeeOther === 'yes' ? [
          { label: "Water Transfer Fee Other Input", value: formData.waterTransferFeeOtherText},
          ] : [],
          { label: "Water Transfer Fee", value: formData.waterTransferFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Utility Transfer Fee", value: formData.utilityTransferFee.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Tax Certificate", value: formData.taxCertFee.replace(/\b\w/g, char => char.toUpperCase()) },
        ]
      },
      {
        title: "Prorations", 
        fields: [
          { label: "Prorate Taxes", value: formData.prorationTaxes.replace(/\b\w/g, char => char.toUpperCase()) },
          ...formData.prorationTaxes === 'prorateOther' ? [
            { label: "Prorate Taxes Other Input", value: formData.prorationTaxesOtherText},
          ] : [],
          { label: "Rents", value: formData.rentsType.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Other Prorations Text", value: formData.otherProrations},
          { label: "Association Assessments", value: formData.associationsAssessments.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Association Assessments Input", value: formData.associationAssessmentsText},
        ]
      },
      {
        title: "Possession", 
        fields: [
          { label: "Agree to Execute a PCOA", value: formData.isPcoa === 'pcoaYes' ? 'Yes' : 'No'},
          { label: "Primary Residence", value: formData.primaryResidence.replace(/\b\w/g, char => char.toUpperCase()) },
          { label: "Fee for Failure to Deliver at Closing", value: `$ ${formData.possessionPerDiem}` },
        ]
      },
      {
        title: "Additional Provisions", 
        fields: [
          { label: "Other Additional Provisions Included", value: formData.additionalProvisions},
        ]
      },
      // Additional sections can be added in the same pattern
  ];
// console.log("Send to Seller", formData.sendToSeller);
// console.log("Listing Agent Email", formData.listingAgentEmail);

return (
  <div className="w-full flex flex-col items-center">
      {/* <ProgressBar currentPage={currentPageValue} subStepProgress={currentSubStepProgress} /> */}
      <MainCard width={750} gap={0}>
          <div className="overflow-y-auto md:max-h-[45vh] max-h-[45vh] w-full">
              <div className="w-full inline-flex justify-between mb-8">
                  {isMobile ? <h3 className="text-black text-[18px] text-left font-normal font-['Inter']">Review & sign</h3> : <h3 className="text-black text-xl text-left font-normal font-['Inter']">Review & sign</h3>}    
                  <div className='gap-2 md:gap-4 items-center inline-flex'>
                      <HelpButton onClick={toggleAssist} />
                      <SaveButton formData={formData} formId={formId}/>
                  </div>
              </div>
              {sections.map((section, index) => (
                  <div key={index} className="mb-8">
                      <div className="flex justify-between items-center align-center w-full">
                          <h1 className="text-[16px] md:text-xl text-shi-deep-purple font-semibold mb-2">{section.title}</h1>
                          <button 
                              // className="border border-shi-green rounded-lg items-center px-4 mt-3 hover:bg-slate-200"
                              className="items-center"
                              onClick={() => handleEditClick(section.title)}
                          >
                              <EditSVG width={22} height={22}/>
                          </button>
                      </div>
                      <hr className="w-full bg-slate-200"/>
                      {section.fields.map((field, index) => (
                          field.sectionLabel ? (
                              <div key={index} className="w-full bg-slate-100 h-auto my-5">
                                  <h3 className="text-[14px] md:text-lg font-[Inter] my-2">{field.sectionLabel}</h3>
                              </div>
                          ) : (
                              <div key={index} className="flex justify-between items-center w-full mt-4">
                                  <div className="flex flex-col text-start">
                                      <h3 className="font-[Inter] text-slate-700 text-sm">{field.label}</h3>
                                      {field.render ? field.render() : <span className="font-[Inter] text-[14px] md:text-[16px] font-semibold text-shi-green">{typeof field.value === 'object' ? JSON.stringify(field.value) : field.value}</span>}
                                  </div>
                              </div>
                          )
                      ))}
                  </div>
              ))}
          </div>
          {/* BOTTOM BUTTONS */}
          <div className="flex flex-col w-full">
              <div className="flex flex-col w-full">
                  <div className="w-full mt-2 justify-start inline-flex">
                      <input 
                          type="checkbox" 
                          onChange={handleSendtoSellerCheckbox} 
                          id="sendToSeller"
                          checked={formData.sendToSeller === 'yes'}
                          disabled={!formData.listingAgentEmail}
                      />
                      {!isEmailValid ? (
                          <div className="inline-flex gap-2 w-full ">
                              <span className="pl-3 font-semibold text-[14px]">Have Shishito Manage e-Signature Process?</span>
                              <button 
                                  className="w-[50px] text-[14px] justify-center border border-shi-green rounded-lg items-center hover:bg-slate-200"
                                  onClick={() => handleEditClick('Parties and Property - Seller Information')}
                              >Edit
                              </button>
                          </div>
                      ) : (
                          <span className="pl-3 font-semibold text-[14px]">Have Shishito Manage e-Signature Process?</span>
                      )}
                  </div>
                  {!isEmailValid && (
                      <div className="inline-flex gap-2 h-auto w-full text-center justify-start mt-2 mb-4">
                          <div className="text-red-500 text-[14px]">
                              Input a valid listing agent email to have Shishito handle e-signature of contract.
                          </div>
                      </div>
                  )}
              </div>
              <div className="w-full text-[14px] mt-2 justify-start inline-flex">
                  <input 
                      type="checkbox" 
                      onChange={handleTermsAndConditionsCheckbox} 
                      id="termsAndConditions"
                      checked={formData.termsAndConditions === 'yes'}
                  />
                  <span className="pl-3 font-semibold text-[14px]">
                      Agree to <a href="/TermsofUse" className="text-blue-500 hover:text-blue-700">Terms of Use</a>?
                  </span>
              </div>
          </div>
          <div className="inline-flex w-full justify-center gap-10 md:gap-[100px] mt-4 bg-white">
              <button className="w-auto text-center text-shi-green bg-white border-white text-[14px] font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-center md:items-start flex" onClick={handleBack}>
                  Back
              </button>
              <div className="">
                <button 
                  onClick={handleAISummarize}
                  className="w-auto text-[14px] font-normal font-['Inter'] p-1.5 border bg-white border-shi-green text-shi-green rounded-md hover:bg-slate-300 inline-flex items-center"
                  disabled={analyzeLoading}
                >
                  <AISVG width={25} height={25}/>
                  {analyzeLoading ? "Processing..." : "Analyze with AI"}
                </button>
                {analysisError && <p style={{color: 'red'}}>{analysisError}</p>}
              </div>
              {showAnalyzer && analysisFileUrl && (
                <Modal onClose={() => setShowAnalyzer(false)}>
                  <ReContractAnalyzerAgent fileUrl={analysisFileUrl} fileName="Contract.pdf" fileKey={latestFileKey}/>
                </Modal>
              )}
              <button 
                  className={`w-auto text-center text-white text-[14px] font-['Inter'] p-1.5 rounded-md border flex ${formData.termsAndConditions !== 'yes' ? 'bg-slate-200' : 'bg-shi-green hover:bg-green-950'}`} 
                  onClick={onNext} 
                  disabled={formData.termsAndConditions !== 'yes' || loading}>
                  {loading ? 'Loading...' : 'Sign and Send'}
              </button>
              {/* <object id="pdfObject" type="application/pdf" className='w-[0px] h-[0px]'></object> */}
          </div>
      </MainCard>
      {isAssistVisible && (
          <div className="w-full md:w-auto absolute bottom-1 md:right-4">
              <ShishitoAssist onClose={toggleAssist}/>
          </div>
      )}
  </div>
);
};

export default FinalReview;